import { BrandInterface, LocationPageInterface, LocationScope, LocationVarKey } from "@swivl-mono/types";
import { Button, ButtonColor, Tooltip } from "@swivl-mono/ui";
import { useMemo } from "react";
import QRCode from "react-qr-code";

import { DropdownRow, InputRow } from "../../../Components/Worksheets/WorksheetComponent/WorksheetComponents";
import { useMergedLocationVariables } from "../../../Hooks/useLocationVariables";

interface Props {
  page: LocationPageInterface;
  brands?: { [brandId: number]: BrandInterface };
}

/**
 * Property Manager v3 → spot.is — the sidebar (ENG-3138; Chuck, 2026-08-30).
 *
 * The v2 sidebar, duplicated, with every input pointed at the `Cortex*` twin the bot reads:
 * `CortexSpotBrandSlug`, `CortexSpotLocationSlug`, `CortexSpotLinkSlug`, and the bot-only
 * `CortexSpotReferralURL`. The v2 page and the legacy keys are untouched — the spot server still
 * resolves on those — and the migration tool backfills the twins from them.
 */
export function SpotV3SidebarControls(props: Props) {
  const { page, brands } = props;
  const vars = useMergedLocationVariables(
    [LocationVarKey.CortexSpotBrandSlug, LocationVarKey.CortexSpotLinkSlug, LocationVarKey.CortexSpotLocationSlug],
    page.groupId,
    page.locationId
  );

  const brand = useMemo(() => {
    if (brands && vars && vars[LocationVarKey.CortexSpotBrandSlug]) {
      return Object.values(brands).find((b) => b.spotSlug === vars[LocationVarKey.CortexSpotBrandSlug].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands, page.locationId, vars]);

  const downloadSVG = () => {
    const svg = document.getElementById("qrcode-v3");
    if (!svg) return;
    const filename = vars ? vars[LocationVarKey.CortexSpotLocationSlug]?.value : undefined;
    const svgData = new XMLSerializer().serializeToString(svg as Node);
    const downloadLink = document.createElement("a");
    downloadLink.download = filename || "qrcode";
    downloadLink.href = "data:image/svg+xml;base64," + btoa(svgData);
    downloadLink.click();
  };

  const url =
    page.type === LocationScope.LOCATION && brand && vars && vars[LocationVarKey.CortexSpotLinkSlug]?.value
      ? "https://my.spot.is/" + brand.spotLinkSlug + "/" + vars[LocationVarKey.CortexSpotLinkSlug].value + "/qr"
      : undefined;

  return (
    <div className="p-5">
      <div className="text-lg font-bold">Spot</div>
      <div className="text-sm text-gray-500">Drag and drop buttons to add them to the spot</div>

      {page.type === LocationScope.LOCATION && (
        <InputRow {...page} marginTop brand={brand} label="Spot Location URL" keyName={LocationVarKey.CortexSpotLocationSlug} />
      )}
      {page.type === LocationScope.LOCATION && (
        <InputRow {...page} marginTop brand={brand} label="Spot Location Short Link URL" keyName={LocationVarKey.CortexSpotLinkSlug} />
      )}

      {brands && (
        <DropdownRow
          {...page}
          marginTop
          label="Brand"
          keyName={LocationVarKey.CortexSpotBrandSlug}
          options={Object.values(brands).map((b) => ({ label: b.name, value: b.spotSlug }))}
        />
      )}

      <InputRow
        {...page}
        marginTop
        label="Referral Link"
        tooltip="Leave blank and the bot sends this location's spot referral page."
        keyName={LocationVarKey.CortexSpotReferralURL}
      />

      {url && (
        <>
          <div className="mb-3 truncate text-orange-500">
            <label>Short Link</label>
            <Tooltip classNames={{ wrapper: "truncate" }} content={url}>
              <a href={url} target="_blank" rel="noreferrer">
                {url}
              </a>
            </Tooltip>
          </div>
          <div className="mb-3">
            <label>QR Code</label>
            <QRCode id="qrcode-v3" size={256} style={{ height: "auto", maxWidth: "100%", width: "100%" }} value={url} viewBox={`0 0 256 256`} />
          </div>
          <Button color={ButtonColor.ORANGE} onClick={downloadSVG}>
            Download QR Code
          </Button>
        </>
      )}
    </div>
  );
}
